import { manifestPath, PWA_APPS, type PwaApp } from "./apps";

export const DISMISSAL_KEY = "tamariba:pwa-install:hidden-until";
export const DISMISSAL_DURATION = 90 * 24 * 60 * 60 * 1000;
export const MANIFEST_PATH = manifestPath("player");

type InstallPrompt = Event & {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
};
type RelatedApp = { platform: string; url?: string; id?: string };
type InstallNavigator = Navigator & {
  standalone?: boolean;
  getInstalledRelatedApps?: () => Promise<RelatedApp[]>;
};
export type InstallState = {
  ready: boolean;
  installed: boolean;
  hiddenUntil: number;
};
export const INITIAL_STATE: InstallState = {
  ready: false,
  installed: false,
  hiddenUntil: 0,
};

export function readHiddenUntil(value: string | null, now: number): number {
  const timestamp = Number(value);
  return Number.isFinite(timestamp) && timestamp > now ? timestamp : 0;
}

export function isOwnApp(
  app: RelatedApp,
  origin: string,
  identity: PwaApp = "player",
): boolean {
  if (app.platform !== "webapp") return false;
  try {
    return (
      (app.url !== undefined &&
        new URL(app.url, origin).href ===
          new URL(manifestPath(identity), origin).href) ||
      (app.id !== undefined &&
        new URL(app.id, origin).href ===
          new URL(PWA_APPS[identity].path, origin).href)
    );
  } catch {
    return false;
  }
}

// Keep installation events above the home/profile pages so navigation and the
// player session request cannot discard the browser's one-use prompt.
export class InstallController {
  private state = INITIAL_STATE;
  private listeners = new Set<() => void>();
  private promptEvent: InstallPrompt | null = null;
  private installationObserved = false;
  private unsavedHiddenUntil = 0;
  private generation = 0;
  private expiryTimer?: number;
  private media: MediaQueryList;

  constructor(
    private win: Window,
    private now = Date.now,
    private app: PwaApp = "player",
  ) {
    this.media = win.matchMedia(
      "(display-mode: standalone), (display-mode: minimal-ui), (display-mode: window-controls-overlay)",
    );
  }

  getSnapshot = () => this.state;

  subscribe = (listener: () => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  private update(patch: Partial<InstallState>) {
    this.state = { ...this.state, ...patch };
    for (const listener of this.listeners) listener();
  }

  private standalone() {
    return (
      this.media.matches ||
      (this.win.navigator as InstallNavigator).standalone === true
    );
  }

  private readDismissal() {
    try {
      return Math.max(
        readHiddenUntil(
          this.win.localStorage.getItem(DISMISSAL_KEY),
          this.now(),
        ),
        readHiddenUntil(String(this.unsavedHiddenUntil), this.now()),
      );
    } catch {
      // Storage can be disabled. Keep the in-memory choice for this page.
      return readHiddenUntil(String(this.state.hiddenUntil), this.now());
    }
  }

  private scheduleExpiry() {
    this.win.clearTimeout(this.expiryTimer);
    const remaining = this.state.hiddenUntil - this.now();
    if (remaining > 0) {
      // Browser timers cannot represent all 90 days in one timeout.
      this.expiryTimer = this.win.setTimeout(
        this.refresh,
        Math.min(remaining, 2_147_483_647),
      );
    }
  }

  refresh = async () => {
    const generation = ++this.generation;
    this.update({ hiddenUntil: this.readDismissal() });
    this.scheduleExpiry();
    if (this.standalone() || this.installationObserved) {
      this.update({ ready: true, installed: true });
      return;
    }
    const navigator = this.win.navigator as InstallNavigator;
    try {
      const apps = (await navigator.getInstalledRelatedApps?.()) ?? [];
      if (generation !== this.generation) return;
      this.update({
        ready: true,
        installed: apps.some((app) =>
          isOwnApp(app, this.win.location.origin, this.app),
        ),
      });
    } catch {
      if (generation === this.generation) {
        this.update({ ready: true, installed: false });
      }
    }
  };

  private onPrompt = (event: Event) => {
    event.preventDefault();
    ++this.generation;
    this.promptEvent = event as InstallPrompt;
    this.installationObserved = false;
    this.update({ ready: true, installed: this.standalone() });
  };

  private onInstalled = () => {
    ++this.generation;
    this.promptEvent = null;
    this.installationObserved = true;
    this.update({ ready: true, installed: true });
  };

  start() {
    this.win.addEventListener("beforeinstallprompt", this.onPrompt);
    this.win.addEventListener("appinstalled", this.onInstalled);
    this.win.addEventListener("focus", this.refresh);
    this.win.addEventListener("pageshow", this.refresh);
    this.win.addEventListener("storage", this.refresh);
    this.media.addEventListener("change", this.refresh);
    void this.refresh();
  }

  stop() {
    ++this.generation;
    this.win.clearTimeout(this.expiryTimer);
    this.win.removeEventListener("beforeinstallprompt", this.onPrompt);
    this.win.removeEventListener("appinstalled", this.onInstalled);
    this.win.removeEventListener("focus", this.refresh);
    this.win.removeEventListener("pageshow", this.refresh);
    this.win.removeEventListener("storage", this.refresh);
    this.media.removeEventListener("change", this.refresh);
  }

  dismiss(): boolean {
    const hiddenUntil = this.now() + DISMISSAL_DURATION;
    this.update({ hiddenUntil });
    this.scheduleExpiry();
    try {
      this.win.localStorage.setItem(DISMISSAL_KEY, String(hiddenUntil));
      this.unsavedHiddenUntil = 0;
      return true;
    } catch {
      this.unsavedHiddenUntil = hiddenUntil;
      return false;
    }
  }

  async install(): Promise<"accepted" | "dismissed" | "unavailable"> {
    const event = this.promptEvent;
    this.promptEvent = null;
    if (!event || this.state.installed) return "unavailable";
    try {
      await event.prompt();
      const { outcome } = await event.userChoice;
      if (outcome === "accepted") this.onInstalled();
      return outcome;
    } catch {
      return "unavailable";
    }
  }
}
